import { Stack, Text, ActionIcon, Group, Box, Badge, Loader, Tooltip, Avatar, ThemeIcon } from '@mantine/core'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { useMutation, useQuery } from '@apollo/client/react'
import RoomQueue from '../graphql/queries/RoomQueue'
import useCurrentRoom from '../hooks/useCurrentRoom'
import useUserIsCurator from '../hooks/useUserIsCurator'
import RemoveRequest from '../graphql/queries/RemoveRequest'
import { useState, useEffect } from 'react'
import defaultProfileIcon from '../img/default-profile.jpg'
import TrackLinkPopover from '../ui/TrackLinkPopover'
import Joyride, { STATUS } from 'react-joyride'
import useCurrentUser from '../hooks/useCurrentUser'
import { Sell } from '@mui/icons-material'

export default function QueueDrawer({ opened, onClose }) {

    const dispatch = useAppDispatch()
    const room = useCurrentRoom()
    const user = useCurrentUser()
    const { data } = useQuery(RoomQueue, { fetchPolicy: 'cache-first', skip: !room, variables: { roomId: room?.id }})
    const isAdmin = useUserIsCurator()
    const [ removeTrackMutation ] = useMutation(RemoveRequest)
    const queue = data?.room.queue.filter( entry => entry.track ) ?? []
    const [removingIndex, setRemovingIndex] = useState(null)
    const [showPlaceholder, setShowPlaceholder] = useState(false)

    // Onboarding tour state
    const [runTour, setRunTour] = useState(false)
    const [hasSeenQueueOnboarding, setHasSeenQueueOnboarding] = useState(true)

    // Check if curator needs queue onboarding
    useEffect(() => {
        if (user?.id && isAdmin) {
            const seen = !!localStorage.getItem(`onboarding-queue-seen-${user.id}`)
            setHasSeenQueueOnboarding(seen)

            // Start tour when drawer opens for the first time
            if (!seen && opened && queue.length > 0) {
                setTimeout(() => setRunTour(true), 500)
            }
        }
    }, [user?.id, isAdmin, opened, queue.length])

    // Stop tour if drawer is closed
    useEffect(() => {
        if (!opened && runTour) {
            setRunTour(false)
        }
    }, [opened, runTour])

    const queueTourSteps = [
        {
            target: '[data-tour="add-to-queue"]',
            content: "Click here to search for music to add to the room's play queue.",
            disableBeacon: true,
        },
        {
            target: '[data-tour="dig-for-music"]',
            content: "Click this button to 'dig' for tracks based on tags. It's a great way to discover music!",
            disableBeacon: true,
        },
        {
            target: '[data-tour="remove-track"]',
            content: "Click this icon to remove a track from the queue. If you remove the track that's currently playing we'll skip to the next track in the queue.",
            disableBeacon: true,
        },
    ]

    const handleJoyrideCallback = (data) => {
        const { status } = data

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Mark queue onboarding as seen
            if (user?.id) {
                localStorage.setItem(`onboarding-queue-seen-${user.id}`, 'true')
                setHasSeenQueueOnboarding(true)
            }
            setRunTour(false)
        }
    }

    async function removeTrack(index) {
        if (!room) return

        setRemovingIndex(index)

        // Show placeholder if removing from a 2-item queue
        const needsPlaceholder = queue.length === 2
        if (needsPlaceholder) {
            setShowPlaceholder(true)
        }

        try {
            const entry = queue[index]
            // Start mutation and animation in parallel
            const mutationPromise = removeTrackMutation({
                refetchQueries: [RoomQueue],
                awaitRefetchQueries: true,
                variables: {roomId: room.id, requestId: entry.id}
            })

            // Wait for fade-out animation
            await new Promise(resolve => setTimeout(resolve, 300))

            // Wait for mutation to complete
            await mutationPromise
        } catch (error) {
            // ignore
        } finally {
            setRemovingIndex(null)
            setShowPlaceholder(false)
        }
    }

    function showTrack(track) {
        dispatch(Actions.showTrackModal(track.id))
    }

    return (
        <>
            <Joyride
                steps={queueTourSteps}
                run={runTour && isAdmin && !hasSeenQueueOnboarding}
                continuous
                hideCloseButton
                showSkipButton
                disableOverlayClose
                disableCloseOnEsc
                locale={{ skip: 'Exit', last: 'Finish' }}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        primaryColor: '#ec03fc',
                        zIndex: 10001,
                    },
                }}
            />
            {/* Backdrop overlay */}
            {opened && (
                <div
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 199,
                        transition: 'opacity 200ms ease-in-out',
                        opacity: opened ? 1 : 0
                    }}
                />
            )}

            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '500px',
                    maxWidth: '85vw',
                    backgroundColor: 'var(--mantine-color-dark-7)',
                    zIndex: 200,
                    transform: opened ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 200ms ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: opened ? '-4px 0 10px rgba(0, 0, 0, 0.3)' : 'none'
                }}
            >
                <Box
                    p="sm"
                    style={{
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>queue_music</span>
                    <Group gap="xs">
                        { isAdmin &&
                        <>
                        <Tooltip label="Add a track from YouTube" position="bottom" openDelay={1000}>
                            <ActionIcon
                                variant="subtle"
                                onClick={() => dispatch(Actions.showYouTubeSearchModal())}
                                aria-label="Add from YouTube"
                            >
                                <span className="material-symbols-outlined">add</span>
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Search for music to add to queue" position="bottom" openDelay={1000}>
                            <ActionIcon
                                variant="subtle"
                                onClick={() => dispatch(Actions.showSearchModal())}
                                aria-label="Search music"
                                data-tour="add-to-queue"
                            >
                                <span className="material-symbols-outlined">search</span>
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Dig for music by tags" position="bottom" openDelay={1000}>
                            <ActionIcon
                                variant="subtle"
                                onClick={() => dispatch(Actions.showDigModal())}
                                aria-label="Dig for music"
                                data-tour="dig-for-music"
                            >
                                <span className="material-symbols-outlined">shuffle</span>
                            </ActionIcon>
                        </Tooltip>
                        </>
                        }
                        <ActionIcon
                            variant="subtle"
                            onClick={onClose}
                            aria-label="Close drawer"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </ActionIcon>
                    </Group>
                </Box>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--mantine-spacing-sm)' }}>
                    <Stack gap="xs">
                        {queue.length === 0 ? (
                            <Box p="md">
                                <Text c="dimmed" ta="center">Queue is empty</Text>
                            </Box>
                        ) : (
                            <>
                                {queue.map((entry, index) => (
                                    <Box key={`${entry.track.id}-q${index}`}>
                                        {/* Now Playing badge - outside and above first card */}
                                        {index === 0 && (
                                            <Box mb="xs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Badge color="pink" variant="light" size="sm">
                                                    Now Playing
                                                </Badge>
                                                {room?.spinning && (
                                                    <Group gap={4} style={{ flexShrink: 0 }}>
                                                        <Text
                                                            size="12px"
                                                            c="gray.1"
                                                            style={{ fontStyle: 'italic' }}
                                                        >
                                                            {room.spinning}
                                                        </Text>
                                                        <ThemeIcon
                                                            variant="subtle"
                                                            size={16}
                                                            style={{ opacity: 0.7 }}
                                                        >
                                                            <Sell style={{ fontSize: '16px' }} />
                                                        </ThemeIcon>
                                                    </Group>
                                                )}
                                            </Box>
                                        )}

                                        {/* Coming up badge - between first and second card */}
                                        {index === 1 && queue.length > 1 && (
                                            <Box mb="xs" mt="md" style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                <Badge color="indigo" variant="light" size="sm">
                                                    Coming up
                                                </Badge>
                                            </Box>
                                        )}

                                        <Box
                                            style={{
                                                backgroundColor: 'var(--mantine-color-dark-6)',
                                                borderRadius: 'var(--mantine-radius-md)',
                                                opacity: removingIndex === index ? 0 : 1,
                                                maxHeight: removingIndex === index ? 0 : '1000px',
                                                overflow: 'hidden',
                                                padding: removingIndex === index ? 0 : 'var(--mantine-spacing-sm)',
                                                marginBottom: removingIndex === index ? 0 : undefined,
                                                transition: removingIndex === index
                                                    ? 'opacity 300ms ease-out, max-height 300ms ease-out 0ms, padding 300ms ease-out 0ms, margin 300ms ease-out 0ms'
                                                    : 'background-color 200ms ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (removingIndex !== index) {
                                                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-dark-5)'
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (removingIndex !== index) {
                                                    e.currentTarget.style.backgroundColor = 'var(--mantine-color-dark-6)'
                                                }
                                            }}
                                        >
                                            <Group gap="sm" wrap="nowrap" align="center" style={{ height: '64px' }}>
                                                {/* Cover Art */}
                                                <div
                                                    style={{
                                                        width: 56,
                                                        height: 56,
                                                        borderRadius: 'var(--mantine-radius-sm)',
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: entry.track.coverArt?.thumb250 ? 'transparent' : '#666',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => showTrack(entry.track)}
                                                >
                                                    {entry.track.coverArt?.thumb250 ? (
                                                        <img
                                                            src={entry.track.coverArt.thumb250}
                                                            alt={`${entry.track.album} cover art`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src="/tunistry-note-icon-white.svg"
                                                            alt="Tunistry logo"
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                opacity: 0.7,
                                                            }}
                                                        />
                                                    )}
                                                </div>

                                                {/* Track Info */}
                                                <Box style={{ flex: 1, minWidth: 0 }}>
                                                    <Text
                                                        fw={500}
                                                        size="sm"
                                                        style={{
                                                            cursor: 'pointer',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                        className="NavLink"
                                                    >
                                                        <TrackLinkPopover track={entry.track} roomId={room.id} />
                                                    </Text>
                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {entry.track.artist}
                                                    </Text>
                                                </Box>

                                                {/* User Avatar and Actions */}
                                                <Group gap="xs" wrap="nowrap" align="center">
                                                {entry.user && (
                                                    <Tooltip label={`Requested by ${entry.user.username}`} position="left">
                                                        <Avatar
                                                            src={entry.user.avatar || defaultProfileIcon}
                                                            alt={entry.user.username}
                                                            size={32}
                                                            radius="sm"
                                                            style={{
                                                                cursor: 'pointer',
                                                                flexShrink: 0,
                                                                border: '2px solid var(--mantine-color-dark-4)',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                    {isAdmin && (
                                                        <Tooltip label="Remove track from queue" position="left" openDelay={1000}>
                                                            <ActionIcon
                                                                variant="subtle"
                                                                color="red"
                                                                onClick={() => removeTrack(index)}
                                                                aria-label="Remove track"
                                                                disabled={removingIndex !== null}
                                                                style={{ flexShrink: 0 }}
                                                                data-tour={index === 0 ? "remove-track" : undefined}
                                                            >
                                                                <span className="material-symbols-outlined">delete</span>
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    )}
                                                </Group>
                                            </Group>
                                        </Box>
                                    </Box>
                                ))}
                                {showPlaceholder && (
                                    <Box
                                        style={{
                                            backgroundColor: 'var(--mantine-color-dark-6)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            padding: 'var(--mantine-spacing-md)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minHeight: '80px',
                                        }}
                                    >
                                        <Loader size="md" />
                                    </Box>
                                )}
                            </>
                        )}
                    </Stack>
                </div>
            </div>
        </>
    )
}

